import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PaymentProviders } from "global/enums";
import { IGoCardlessService } from "services/GoCardlessService";
import { IPaymentProvidersService } from "services/PaymentProvidersService";
import { GoCardlessSettingsModel, PaymentProviderModel, BlinkSettingsModel } from "services/api/cplapi/CPLAPI";
import { RootState } from "setup";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import Blink from "../pages/Blink";
import GoCardless from "../pages/GoCardless";
import { NavigationListRoute } from "cpl-react-common/components/Navigation/NavigationList";

export type PaymentProviderComponentProps = {
  displayName: string;
};

export type PaymentProviderRoute = NavigationListRoute & {
  paymentProviderID: number;
};

export const paymentProviderRoutes: PaymentProviderRoute[] = [
  {
    paymentProviderID: PaymentProviders.Blink,
    name: "Blink",
    component: <Blink displayName="Blink" />,
    subRoute: "Blink",
    "data-test-id": "payment-providers-nav__blink-button",
  },
  {
    paymentProviderID: PaymentProviders.GoCardless,
    name: "GoCardless",
    component: <GoCardless displayName="GoCardless" />,
    subRoute: "GoCardless",
    "data-test-id": "payment-providers-nav__gocardless-button",
  },
].sort((a, b) => a.name.localeCompare(b.name));

type PaymentProvidersInitialState = {
  EnabledPaymentProviderRouteIndex?: number;
  PaymentProviders: Array<PaymentProviderModel>;
  GoCardlessSettings: GoCardlessSettingsModel;
  BlinkSettings: BlinkSettingsModel;
  Loading: boolean;
};

const initialState: PaymentProvidersInitialState = {
  EnabledPaymentProviderRouteIndex: undefined,
  PaymentProviders: new Array<PaymentProviderModel>(),
  GoCardlessSettings: new GoCardlessSettingsModel({ emailAddress: "", paymentDescription: "" }),
  BlinkSettings: new BlinkSettingsModel({ paymentDescription: "" }),
  Loading: false,
};

export const paymentProvidersSlice = createSlice({
  name: "PaymentProviders",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getPaymentProviders.pending, (state) => {
        state.Loading = true;
      })
      .addCase(getPaymentProviders.fulfilled, (state, action) => {
        const enabledProvider = action.payload.find((x) => x.enabled);
        if (enabledProvider !== undefined) {
          state.EnabledPaymentProviderRouteIndex = paymentProviderRoutes.findIndex(
            (x) => x.paymentProviderID === enabledProvider.paymentProviderID
          );
        }
        state.PaymentProviders = action.payload;
        state.Loading = false;
      })
      .addCase(getPaymentProviders.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(getGoCardlessSettings.pending, (state) => {
        state.Loading = true;
      })
      .addCase(getGoCardlessSettings.fulfilled, (state, action) => {
        state.GoCardlessSettings = action.payload;
        state.Loading = false;
      })
      .addCase(getGoCardlessSettings.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(getBlinkSettings.pending, (state) => {
        state.Loading = true;
      })
      .addCase(getBlinkSettings.fulfilled, (state, action) => {
        state.BlinkSettings = action.payload;
        state.Loading = false;
      })
      .addCase(getBlinkSettings.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(authoriseGoCardlessMerchant.pending, (state) => {
        state.Loading = true;
      })
      .addCase(authoriseGoCardlessMerchant.fulfilled, (state) => {
        state.Loading = false;
      })
      .addCase(authoriseGoCardlessMerchant.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(enablePaymentProvider.pending, (state) => {
        state.Loading = true;
      })
      .addCase(enablePaymentProvider.fulfilled, (state) => {
        state.Loading = false;
      })
      .addCase(enablePaymentProvider.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(disablePaymentProvider.pending, (state) => {
        state.Loading = true;
      })
      .addCase(disablePaymentProvider.fulfilled, (state) => {
        state.Loading = false;
      })
      .addCase(disablePaymentProvider.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(updateGoCardlessSettings.pending, (state) => {
        state.Loading = true;
      })
      .addCase(updateGoCardlessSettings.fulfilled, (state) => {
        state.Loading = false;
      })
      .addCase(updateGoCardlessSettings.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(updateBlinkSettings.pending, (state) => {
        state.Loading = true;
      })
      .addCase(updateBlinkSettings.fulfilled, (state) => {
        state.Loading = false;
      })
      .addCase(updateBlinkSettings.rejected, (state) => {
        state.Loading = false;
      })
      .addCase(revokeGoCardlessAccessToken.pending, (state) => {
        state.Loading = true;
      })
      .addCase(revokeGoCardlessAccessToken.fulfilled, (state) => {
        state.Loading = false;
      })
      .addCase(revokeGoCardlessAccessToken.rejected, (state) => {
        state.Loading = false;
      });
  },
});

export const getPaymentProviders = createAsyncThunk("PaymentProviders/GetPaymentProviders", async () => {
  const paymentProvidersService = container.get<IPaymentProvidersService>(SERVICE_TYPES.PaymentProvidersService);
  return await paymentProvidersService.getPaymentProviders();
});

export const getGoCardlessSettings = createAsyncThunk("PaymentProviders/GetGoCardlessSettings", async () => {
  const paymentProvidersService = container.get<IPaymentProvidersService>(SERVICE_TYPES.PaymentProvidersService);
  return await paymentProvidersService.getGoCardlessSettings();
});

export const getBlinkSettings = createAsyncThunk("PaymentProviders/GetBlinkSettings", async () => {
  const paymentProvidersService = container.get<IPaymentProvidersService>(SERVICE_TYPES.PaymentProvidersService);
  return await paymentProvidersService.getBlinkSettings();
});

export const authoriseGoCardlessMerchant = createAsyncThunk(
  "PaymentProviders/AuthoriseGoCardlessMerchant",
  async () => {
    const goCardlessService = container.get<IGoCardlessService>(SERVICE_TYPES.GoCardlessService);
    return await goCardlessService.authoriseMerchant();
  }
);

export const revokeGoCardlessAccessToken = createAsyncThunk(
  "PaymentProviders/RevokeGoCardlessAccessToken",
  async (_, thunkAPI) => {
    const goCardlessService = container.get<IGoCardlessService>(SERVICE_TYPES.GoCardlessService);
    await goCardlessService.revokeAccessToken();
    thunkAPI.dispatch(getPaymentProviders());
  }
);

export const enablePaymentProvider = createAsyncThunk(
  "PaymentProviders/EnablePaymentProvider",
  async (paymentProvider: PaymentProviders, thunkAPI) => {
    const paymentProvidersService = container.get<IPaymentProvidersService>(SERVICE_TYPES.PaymentProvidersService);
    await paymentProvidersService.enablePaymentProvider(paymentProvider);
    thunkAPI.dispatch(getPaymentProviders());
  }
);

export const disablePaymentProvider = createAsyncThunk(
  "PaymentProviders/DisablePaymentProvider",
  async (_, thunkAPI) => {
    const paymentProvidersService = container.get<IPaymentProvidersService>(SERVICE_TYPES.PaymentProvidersService);
    await paymentProvidersService.disablePaymentProvider();
    thunkAPI.dispatch(getPaymentProviders());
  }
);

export const updateGoCardlessSettings = createAsyncThunk(
  "PaymentProviders/UpdateGoCardlessSettings",
  async (goCardlessSettings: GoCardlessSettingsModel, thunkAPI) => {
    const paymentProvidersService = container.get<IPaymentProvidersService>(SERVICE_TYPES.PaymentProvidersService);
    await paymentProvidersService.updateGoCardlessSettings(goCardlessSettings);
    thunkAPI.dispatch(getGoCardlessSettings());
  }
);

export const updateBlinkSettings = createAsyncThunk(
  "PaymentProviders/UpdateBlinkSettings",
  async (blinkSettings: BlinkSettingsModel, thunkAPI) => {
    const paymentProvidersService = container.get<IPaymentProvidersService>(SERVICE_TYPES.PaymentProvidersService);
    await paymentProvidersService.updateBlinkSettings(blinkSettings);
    thunkAPI.dispatch(getBlinkSettings());
  }
);

export const selectLoading = (state: RootState) => state.paymentProviders.Loading;
export const selectPaymentProviders = (state: RootState) => state.paymentProviders.PaymentProviders;
export const selectGoCardlessSettings = (state: RootState) => state.paymentProviders.GoCardlessSettings;
export const selectBlinkSettings = (state: RootState) => state.paymentProviders.BlinkSettings;
export const selectEnabledPaymentProviderRouteIndex = (state: RootState) =>
  state.paymentProviders.EnabledPaymentProviderRouteIndex;

export default paymentProvidersSlice.reducer;
